/**
 * ตรวจไฟล์ก่อนส่งขึ้น server
 *
 * เจตนาคือ "ตัดจบเฉพาะกรณีที่มั่นใจว่าผิดแน่ ๆ" — ไฟล์ว่าง ไฟล์ใหญ่เกิน
 * นามสกุลผิด หรือไฟล์ที่ถูกเปลี่ยนนามสกุลมา (ข้างในไม่ใช่ Excel จริง)
 * ทั้งหมดนี้รู้ได้จากไม่กี่ไบต์แรก ไม่ต้องอัปโหลดทั้งไฟล์ขึ้นไปก่อน
 *
 * ส่วนการตรวจ "รูปแบบ template ถูกไหม" (มีชีต ผ.1 / มีหัวตารางวัน / รหัสร้าน
 * 10 ตัว) จงใจไม่ทำซ้ำที่ฝั่งนี้ — ปล่อยให้ขั้นตอน "ตรวจสอบไฟล์" (preview)
 * เป็นคนตอบ เพราะนั่นคือ parser ตัวจริงที่จะอ่านไฟล์นี้เข้าฐานข้อมูล
 * ถ้าเขียนกฎชุดที่สองไว้ที่ browser เมื่อไรที่สองฝั่งไม่ตรงกัน ผู้ใช้จะเจอ
 * อาการที่แย่กว่าเดิม คือถูกบล็อกทั้งที่ไฟล์ใช้ได้ (หรือกลับกัน)
 * และ preview ก็ยังไม่เขียนอะไรลงฐานข้อมูลอยู่แล้ว
 */

export const PATHWAY_MAX_FILE_SIZE = 30 * 1024 * 1024;
export const PATHWAY_ACCEPTED_EXTENSIONS = ["xlsx", "xls"] as const;

/** อ่านหัวไฟล์แค่นี้พอ: local file header ของ zip ยาว 30 ไบต์ + ชื่อ entry แรก */
const HEAD_BYTES = 256;

const ZIP_MAGIC = [0x50, 0x4b, 0x03, 0x04];
const OLE2_MAGIC = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];

/** ชื่อไฟล์แรกใน .xlsx ที่โปรแกรม spreadsheet ทุกตัวเขียนออกมา */
const OOXML_ENTRY_PREFIXES = ["[Content_Types].xml", "_rels/", "xl/", "docProps/"];

const startsWith = (head: Uint8Array, magic: number[]): boolean =>
  head.length >= magic.length && magic.every((byte, i) => head[i] === byte);

export const extensionOf = (fileName: string): string => {
  const dot = fileName.lastIndexOf(".");
  return dot < 0 ? "" : fileName.slice(dot + 1).toLowerCase();
};

/**
 * อ่านชื่อ entry แรกของ zip: [0..3] signature, [26..27] ความยาวชื่อ, [30..] ชื่อ
 * คืน null เมื่ออ่านไม่ได้ — ผู้เรียกต้องปล่อยผ่าน ไม่ใช่บล็อก
 */
const firstZipEntryName = (head: Uint8Array): string | null => {
  if (head.length < 30) return null;

  const nameLength = head[26] | (head[27] << 8);
  if (nameLength <= 0 || nameLength > 255) return null;
  if (head.length < 30 + nameLength) return null;

  let name = "";
  for (let i = 0; i < nameLength; i++) name += String.fromCharCode(head[30 + i]);
  return name;
};

/**
 * ตรวจจากไบต์ต้นไฟล์ว่าเป็นไฟล์ spreadsheet จริงไหม
 * คืนข้อความภาษาไทยเมื่อไม่ผ่าน คืน null เมื่อผ่าน (หรือเมื่อไม่แน่ใจ)
 */
export const checkSpreadsheetHead = (head: Uint8Array): string | null => {
  // .xls เก่าเป็น OLE2 compound file — รับได้ตามที่ API รับ
  if (startsWith(head, OLE2_MAGIC)) return null;

  if (!startsWith(head, ZIP_MAGIC)) {
    return "ไฟล์นี้ไม่ใช่ไฟล์ Excel — นามสกุลเป็น .xlsx/.xls แต่เนื้อไฟล์ข้างในไม่ใช่ (อาจเป็นไฟล์อื่นที่ถูกเปลี่ยนนามสกุลมา)";
  }

  const entry = firstZipEntryName(head);
  // อ่านชื่อ entry ไม่ได้ = ไม่แน่ใจ ปล่อยให้ server ตัดสิน
  if (entry === null) return null;

  const isOoxml = OOXML_ENTRY_PREFIXES.some((prefix) => entry.startsWith(prefix));
  return isOoxml
    ? null
    : "ไฟล์นี้เป็นไฟล์บีบอัด (zip) ที่ไม่ใช่ไฟล์ Excel กรุณาเลือกไฟล์ .xlsx ที่บันทึกจาก Excel";
};

/** ตรวจชื่อ/ขนาด — ส่วนที่ไม่ต้องอ่านเนื้อไฟล์ */
export const checkFileNameAndSize = (
  fileName: string,
  size: number,
): string | null => {
  const ext = extensionOf(fileName);
  if (!PATHWAY_ACCEPTED_EXTENSIONS.includes(ext as "xlsx" | "xls")) {
    return ext
      ? `ระบบรับเฉพาะไฟล์ .xlsx และ .xls — ไฟล์ที่เลือกเป็น .${ext}`
      : "ระบบรับเฉพาะไฟล์ .xlsx และ .xls — ไฟล์ที่เลือกไม่มีนามสกุล";
  }

  if (size === 0) return "ไฟล์นี้ว่างเปล่า (0 ไบต์) กรุณาเลือกไฟล์ใหม่";

  if (size > PATHWAY_MAX_FILE_SIZE) {
    const mb = (size / 1024 / 1024).toFixed(1);
    return `ไฟล์มีขนาด ${mb} MB เกินขีดจำกัด 30 MB`;
  }

  return null;
};

/** ไฟล์เพียงบางส่วนที่ตัวตรวจต้องใช้ — ทำให้ทดสอบได้โดยไม่ต้องพึ่ง File จริง */
export interface CheckableFile {
  name: string;
  size: number;
  slice: (start: number, end: number) => { arrayBuffer: () => Promise<ArrayBuffer> };
}

/**
 * ตรวจครบทุกด่านของไฟล์ที่ผู้ใช้เลือก คืนข้อความ error ภาษาไทย หรือ null
 * ถ้าอ่านหัวไฟล์ไม่ได้ (browser เก่า/ไฟล์ถูกย้ายระหว่างเลือก) จะปล่อยผ่าน
 * ไปให้ server ตรวจแทน ดีกว่าบล็อกไฟล์ที่อาจใช้ได้
 */
export const checkPathwayFile = async (
  file: CheckableFile,
): Promise<string | null> => {
  const basic = checkFileNameAndSize(file.name, file.size);
  if (basic !== null) return basic;

  try {
    const head = new Uint8Array(await file.slice(0, HEAD_BYTES).arrayBuffer());
    return checkSpreadsheetHead(head);
  } catch {
    return null;
  }
};
